import dayjs from 'dayjs';
import i18n from '@/i18n';
import store from '@/store';
import H_file from '@/helpers/file';
import H_time from '@/helpers/time';
import H_common from '@/helpers/common';
import H_factoryTag from '@/helpers/factoryTag';
import H_meetingTemplate from '@/helpers/meetingTemplate';
import Spare_meeting from '@/spare/meeting';

export default {
  // 解碼後端儲存的名稱格式（支援多語言：中文、韓文、日文、越南文等）
  decodeName(encodedName) {
    if (!encodedName || typeof encodedName !== 'string') {
      return encodedName;
    }
    try {
      // 嘗試使用 decodeURIComponent 解碼（處理 URL encoding）
      // 這可以正確處理各種 Unicode 字符（中文、韓文、日文、越南文等）
      return decodeURIComponent(encodedName);
    } catch (e) {
      // 如果解碼失敗（可能已經是解碼過的或格式不對），返回原始值
      return encodedName;
    }
  },
  setIndex(rawDataList) {
    return rawDataList.map(({
      id,
      name,
      factory_tags,
      updated_at,
      expired_at,
      owner,
    }) => {
      return {
        id,
        name: {
          text: name,
          chips: H_common.setFactoryTagsInDataTable(factory_tags),
        },
        owner,
        updatedAt: H_time.getDateDashFormat(updated_at, 'YYYY-MM-DD HH:mm:ss'),
        deadline: H_time.getDateDashFormat(expired_at, 'YYYY-MM-DD HH:mm:ss'),
      };
    });
  },
  setExportRecordParams(filterState) {
    const { search, convertStatus, duration, createdUser, meetingExportType } = filterState;
    const { start, end } = duration;
    let params = {
      order_by: 'created_at',
      order_way: 'desc',
    };
    if (search) params.search = search;
    if (convertStatus) params.convert_status = convertStatus.join();
    if (meetingExportType) params.type = meetingExportType.join();
    if (start && end) {
      params = {
        ...params,
        time_field: 'created_at',
        start_time: start,
        end_time: end,
      };
    }
    if (createdUser) params.created_user = createdUser.join();
    return params;
  },
  setExportRecordListData(rawDataList) {
    return rawDataList.map(({
      id,
      name,
      type,
      created_at,
      created_user,
      convert_status,
      convert_pdf_attach,
    }) => {
      const data = {
        id,
        name: {
          text: name,
          chips: this.$_getConvertStatusName(convert_status),
        },
        type: this.$_getTypeText(type),
        createdAt: H_time.getDateDashFormat(created_at, 'YYYY-MM-DD HH:mm:ss'),
        createdUser: created_user,
        download: null,
        convertPdfAttach: convert_pdf_attach,
      };
      if (convert_status === 'COMPLETE') data.download = {
        icon: 'icon-cloud-download',
        tooltipText: '下載',
      };
      return data;
    });
  },
  $_getTypeText(type) {
    const typeMap = {
      meeting: '議程',
      schedule: '時序表',
    };
    return typeMap[type] || '';
  },
  $_getConvertStatusName(convertStatus) {
    const statusMap = {
      PENDING: [{ name: '處理中', color: 'yellow', value: 'PENDING' }],
      PROCESSING: [{ name: '處理中', color: 'yellow', value: 'PROCESSING' }],
      COMPLETE: [{ name: '已完成', color: 'blue', value: 'COMPLETE' }],
      FAILED: [{ name: '處理失敗', color: 'red', value: 'FAILED' }],
    };
    return statusMap[convertStatus] || [];
  },
  setDownloadRecordParams(filterState) {
    const { downloadAt, downloadUser } = filterState;
    const { start, end } = downloadAt;
    let params = {
      order_by: 'created_at',
      order_way: 'desc',
    };
    if (start && end) {
      params = {
        ...params,
        time_field: 'created_at',
        start_time: start,
        end_time: end,
      };
    }
    if (downloadUser) params.download_user = downloadUser.join();
    return params;
  },
  setDownloadRecordListData(rawDataList) {
    return rawDataList.map(({
      id,
      download_at,
      download_user,
    }) => ({
      id,
      downloadAt: H_time.getDateDashFormat(download_at, 'YYYY-MM-DD HH:mm:ss'),
      downloadUser: download_user,
    }));
  },
  setExportPopupFields({
    coverTemplate = null,
    tableOfContentTemplate = null,
    attachCoverTemplate = null,
    meetingItemGroup = [],
    meetingExportItems = [],
  }) {
    const exportItemsMap = new Map(meetingExportItems.map(item => [item.module, true]));
    const meetingItemExportItemsMap = new Map(meetingExportItems.filter(item => item.module === 'meeting_item').map(item => [item.meetingItem?.id, true]));
    const flattenedMeetingItemGroups = meetingItemGroup.reduce((acc, group) => {
      acc.push({
        ...group,
        isChecked: this.getGroupCheckStatus(group, meetingItemExportItemsMap),
        isIndeterminate: this.getGroupIndeterminateStatus(group, meetingItemExportItemsMap),
        value: 'meetingItemGroup',
      });
      group.children?.forEach(child => {
        const hasExportItem = meetingItemExportItemsMap.has(child.id);
        acc.push({
          ...child,
          parentId: group.id,
          isChecked: hasExportItem,
          value: 'meetingItem',
          name: `${child.caseNumber} ${child.name}`,
          isExportAttach: true,
          isExportResolutionAttach: true,
          // TODO: 待列表 API 提供有無附件後接上，目前一律當作有
          hasAttach: true,
          hasResolutionAttach: true,
        });
      });
      return acc;
    }, []);
    const templateConfigs = [
      { template: coverTemplate, value: 'cover', i18nKey: '封面範本' },
      { template: tableOfContentTemplate, value: 'contents', i18nKey: '目錄範本' },
      { template: attachCoverTemplate, value: 'attach_cover', i18nKey: '附件封面範本' },
    ];
    const resultFields = templateConfigs
      .filter(({ template }) => template?.id)
      .map(({ template, value, i18nKey }) => ({
        id: template.id,
        name: `${i18n.t(i18nKey)}: ${template?.name ?? ''}`,
        isChecked: exportItemsMap.has(value),
        value,
      }));
    return [...resultFields, ...flattenedMeetingItemGroups];
  },
  getGroupCheckStatus(group, meetingItemExportItemsMap) {
    if (!group.children || group.children.length === 0 || meetingItemExportItemsMap.size === 0) return false;
    return group.children.every(child => meetingItemExportItemsMap.has(child.id));
  },
  getGroupIndeterminateStatus(group, meetingItemExportItemsMap) {
    if (!group.children || group.children.length === 0 || meetingItemExportItemsMap.size === 0) return false;
    const isAllItemChecked = group.children.every(child => meetingItemExportItemsMap.has(child.id));
    if (isAllItemChecked) return false;
    return group.children.some(child => meetingItemExportItemsMap.has(child.id));
  },
  setDateFormatItems(res) {
    return res.map(({
      id,
      name,
      value,
    }) => {
      return {
        id,
        textLabel: name,
        value,
      };
    });
  },
  setCaseNumberItems(res) {
    return res.map(({
      id,
      name,
      value,
    }) => {
      return {
        id,
        textLabel: name,
        value,
      };
    });
  },
  // 數字轉中文的 helper 函數
  numberToChinese(num) {
    const chineseNumbers = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
    if (num === 0) {
      return '';
    } else if (num <= 10) {
      return chineseNumbers[num];
    } else if (num < 20) {
      const ones = num % 10;
      return '十' + (ones > 0 ? chineseNumbers[ones] : '');
    } else if (num < 100) {
      const tens = Math.floor(num / 10);
      const ones = num % 10;
      return chineseNumbers[tens] + '十' + (ones > 0 ? chineseNumbers[ones] : '');
    } else if (num < 1000) {
      const hundreds = Math.floor(num / 100);
      const remainder = num % 100;
      return chineseNumbers[hundreds] + '百' + (remainder > 0 ? this.numberToChinese(remainder) : '');
    } else {
      // 超過 1000 的情況
      return num.toString();
    }
  },
  // 根據 regex 判斷格式並生成 itemLabel
  generateItemLabel(regex, num) {
    if (!regex) return;
    // 第[一二三四五六七八九十百]+案
    if (regex === '^第[一二三四五六七八九十百]+案\\\\s?') {
      return `第${this.numberToChinese(num)}案`;
    }
    // 第\d+案
    if (regex === '^第\\\\d+案\\\\s?') {
      return `第${num}案`;
    }
    // \d+.
    if (regex === '^\\\\d+\\\\.\\\\s?') {
      return `${num}.`;
    }
    // [一二三四五六七八九十百]+.
    if (regex === '^[一二三四五六七八九十百]+\\\\.\\\\s?') {
      return `${this.numberToChinese(num)}.`;
    }
    // 預設情況：如果 regex 不匹配已知格式，嘗試用數字
    return `${num}`;
  },
  setSubmitCreateData(rawData) {
    const {
      name,
      chairperson,
      time,
      timeFormat,
      formattedTime,
      location,
      meetingItemTemplate,
      coverTemplate,
      tableOfContentTemplate,
      attachCoverTemplate,
      resolutionTemplate,
      meetingTemplate,
      itemLabelType,
      owner,
      deadline,
      scopeAvatars = [],
      scopeRoles,
      factoryTags,
      remark,
      attaches,
      signInForm,
    } = rawData;
    const {user_factory_roles, user_factory_role_templates} = H_common.setSubmitRolesAndFactoryRoles(scopeRoles);
    const resultData = {
      name,
      remark,
      chairperson,
      meeting_date: time,
      date_format: timeFormat,
      meeting_date_format: formattedTime,
      location,
      owner,
      expired_at: H_time.setTimeToUtc(deadline).format(),
      meeting_item_template: meetingItemTemplate,
      meeting_template: meetingTemplate,
      contents_template: tableOfContentTemplate,
      cover_template: coverTemplate,
      attach_cover_template: attachCoverTemplate,
      resolution_template: resolutionTemplate,
      item_label_type: itemLabelType,
      factory_tags: factoryTags[0],
      attaches: H_file.setSubmitInModule(attaches),
      sign_in_form: H_file.setSubmitInModule(signInForm)[0],
      users: scopeAvatars,
      user_factory_roles,
      user_factory_role_templates,
    };
    return resultData;
  },
  setUpdateShow(rawData) {
    const {
      name,
      chairperson,
      meeting_date,
      date_format,
      meeting_date_format,
      location,
      meeting_item_template,
      cover_template,
      contents_template,
      attach_cover_template,
      resolution_template,
      meeting_template,
      item_label_type,
      owner,
      expired_at,
      users,
      user_factory_roles,
      user_factory_role_templates,
      factory_tags,
      remark,
      sign_in_form,
      attaches,
    } = rawData;
    return {
      name,
      chairperson,
      time: H_time.getDateDashFormat(meeting_date, 'YYYY-MM-DD HH:mm', true),
      timeFormat: date_format?.id ?? '',
      timeFormatValue: date_format?.value ?? '',
      formattedTime: meeting_date_format,
      location,
      meetingItemTemplate: meeting_item_template?.id ?? '',
      coverTemplate: cover_template?.id ?? '',
      tableOfContentTemplate: contents_template?.id ?? '',
      attachCoverTemplate: attach_cover_template?.id ?? '',
      resolutionTemplate: resolution_template?.id ?? '',
      meetingTemplate: meeting_template?.id ?? '',
      itemLabelType: item_label_type?.id ?? '',
      owner: owner?.id ?? '',
      ownerSelectValue: owner,
      deadline: H_time.getDateDashFormat(expired_at),
      scopeAvatars: users.map(user => user.id),
      scopeAvatarsSelectValue: users,
      scopeRoles: [user_factory_roles.map(role => role.id), user_factory_role_templates.map(role => role.id)],
      factoryTags: [factory_tags.map(tag => tag.id)],
      remark,
      attaches: H_file.setShowInModule(attaches),
      signInForm: sign_in_form ? H_file.setShowInModule([sign_in_form]) : [],
    };
  },
  setShow(rawData) {
    const {
      name,
      chairperson,
      meeting_date_format,
      location,
      meeting_item_template,
      cover_template,
      contents_template,
      meeting_template,
      item_label_type = {
        id: '',
        name: '',
        value: '',
      },
      owner,
      expired_at,
      factory_tags,
      remark,
      attaches,
      sign_in_form,
      users,
      user_factory_roles,
      user_factory_role_templates,
      attach_cover_template,
      resolution_template,
      meeting_start_time,
    } = rawData;
    let scopeRoles = user_factory_role_templates.map(role => role.name).join(`, `);
    if (user_factory_roles.length) {
      if (scopeRoles) scopeRoles += `, `;
      scopeRoles += user_factory_roles.map(role => role.name).join(`, `);
    }
    return {
      name,
      chairperson,
      time: meeting_date_format,
      location,
      meetingItemTemplate: this.setShowTemplateInfo(meeting_item_template),
      coverTemplate: this.setShowTemplateInfo(cover_template),
      tableOfContentTemplate: this.setShowTemplateInfo(contents_template),
      attachCoverTemplate: this.setShowTemplateInfo(attach_cover_template),
      resolutionTemplate: this.setShowTemplateInfo(resolution_template),
      meetingTemplate: this.setShowTemplateInfo(meeting_template),
      itemLabelType: item_label_type,
      owner,
      deadline: H_time.getDateDashFormat(expired_at),
      users,
      roles: scopeRoles,
      factoryTags: H_factoryTag.setShowInModule(factory_tags),
      remark,
      attaches: H_file.setShowInModule(attaches),
      signInForm: sign_in_form ? H_file.setShowInModule([sign_in_form]) : [],
      meetingStartTime: H_time.getDateDashFormat(meeting_start_time, 'YYYY-MM-DD HH:mm:ss'),
    };
  },
  setShowTemplateInfo(rawTemplate) {
    if (!rawTemplate) {
      return {
        id: '',
        attach: [],
        variables: [],
      };
    }
    const {
      id,
      name,
      attach,
      meeting_template_items,
      editable_width_px,
    } = rawTemplate;
    const baseInfo = {
      id,
      name,
      attach: attach ? H_file.setShowInModule([attach]) : [],
      editableWidthPx: editable_width_px ?? null,
    };
    if (meeting_template_items !== undefined) {
      baseInfo.variables = H_meetingTemplate.setMeetingTemplateItemsShowInModule(meeting_template_items);
    }
    return baseInfo;
  },
  setSubmitMeetingItemGroupCreateData(rawData) {
    const {
      id,
      name,
      sequence,
      scheduleTemplate,
      tableOfContentTemplateSection,
      meetingTemplateSection,
    } = rawData;
    return {
      id,
      name,
      sequence,
      factory: store.state.app.factory.id,
      schedule_template: scheduleTemplate,
      contents_template_variable: tableOfContentTemplateSection,
      meeting_template_variable: meetingTemplateSection,
    };
  },
  setUpdateMeetingItemGroup(rawData, meetingContent = {}) {
    const { tableOfContentTemplate = {}, meetingTemplate = {} } = meetingContent;
    const {
      id,
      name,
      sequence,
      schedule_template,
      contents_template_variable,
      meeting_template_variable,
    } = rawData;
    const { isTableOfContentSectionInvalid, isMeetingSectionInvalid } = this.isTemplateSectionValid(
      tableOfContentTemplate,
      meetingTemplate,
      contents_template_variable,
      meeting_template_variable,
    );
    return {
      id,
      name,
      sequence,
      scheduleTemplate: schedule_template?.id ?? '',
      tableOfContentTemplateSection: isTableOfContentSectionInvalid ? '' : contents_template_variable?.id ?? '',
      meetingTemplateSection: isMeetingSectionInvalid ? '' : meeting_template_variable?.id ?? '',
    };
  },
  setShowMeetingItemGroup(rawData, content = {}) {
    const { tableOfContentTemplate = {}, meetingTemplate = {} } = content;
    const {
      id,
      name,
      sequence,
      schedule_template,
      contents_template_variable,
      meeting_template_variable,
      meeting_items,
    } = rawData;
    const { isTableOfContentSectionInvalid, isMeetingSectionInvalid } = this.isTemplateSectionValid(
      tableOfContentTemplate,
      meetingTemplate,
      contents_template_variable,
      meeting_template_variable,
    );
    const chipList = this.setGroupChipList(
      isTableOfContentSectionInvalid,
      isMeetingSectionInvalid,
      meeting_items,
    );
    const baseData = {
      id,
      name,
      sequence,
      scheduleTemplate: schedule_template,
      tableOfContentTemplateSection: contents_template_variable,
      isTableOfContentSectionInvalid,
      meetingTemplateSection: meeting_template_variable,
      isMeetingSectionInvalid,
      newScheduleTemplate: schedule_template?.id ?? '',
      newContentsTemplateSection: isTableOfContentSectionInvalid ? '' : contents_template_variable?.id ?? '',
      newMeetingTemplateSection: isMeetingSectionInvalid ? '' : meeting_template_variable?.id ?? '',
      children: this.setIndexMeetingItem(meeting_items),
      chipList,
      templateChipList: this.setTemplateChipList(
        isTableOfContentSectionInvalid,
        isMeetingSectionInvalid,
      ),
    };
    return baseData;
  },
  setIndexMeetingItemGroup(rawDataList, content = {}) {
    const { tableOfContentTemplate = {}, meetingTemplate = {} } = content;
    return rawDataList.map(({
      id,
      name,
      schedule_template,
      contents_template_variable,
      meeting_template_variable,
      meeting_items,
    }) => {
      const { isTableOfContentSectionInvalid, isMeetingSectionInvalid } = this.isTemplateSectionValid(
        tableOfContentTemplate,
        meetingTemplate,
        contents_template_variable,
        meeting_template_variable,
      );
      const chipList = this.setGroupChipList(
        isTableOfContentSectionInvalid,
        isMeetingSectionInvalid,
        meeting_items,
      );
      const baseData = {
        id,
        name,
        isCollapsable: true,
        isLoading: false,
        scheduleTemplate: schedule_template,
        tableOfContentTemplateSection: contents_template_variable,
        isTableOfContentSectionInvalid,
        meetingTemplateSection: meeting_template_variable,
        isMeetingSectionInvalid,
        newScheduleTemplate: schedule_template?.id ?? '',
        newContentsTemplateSection: isTableOfContentSectionInvalid ? '' : contents_template_variable?.id ?? '',
        newMeetingTemplateSection: isMeetingSectionInvalid ? '' : meeting_template_variable?.id ?? '',
        children: this.setIndexMeetingItem(meeting_items),
        chipList,
        templateChipList: this.setTemplateChipList(
          isTableOfContentSectionInvalid,
          isMeetingSectionInvalid,
        ),
      };
      return baseData;
    });
  },
  setTemplateChipList(isTableOfContentSectionInvalid, isMeetingSectionInvalid) {
    return isTableOfContentSectionInvalid || isMeetingSectionInvalid
      ? [{
          text: '重新選擇範本區塊',
          color: 'red',
        }]
      : [];
  },
  setGroupChipList(isTableOfContentSectionInvalid, isMeetingSectionInvalid, childrenMeetingItems) {
    const templateChipList = this.setTemplateChipList(
      isTableOfContentSectionInvalid,
      isMeetingSectionInvalid,
    );
    const failChipList = [{
      text: '處理失敗',
      color: 'red',
    }];
    const processingChipList = [{
      text: '處理中',
      color: 'yellow',
    }];

    let hasFailed = false;
    let hasProcessing = false;

    for (const item of childrenMeetingItems) {
      if (
        item.parse_status === 'FAILED' ||
        this.isResolutionParseFailed(item.resolution_parse_status) ||
        this.isResolutionConvertFailed(item) ||
        item.convert_status === 'FAILED'
      ) {
        hasFailed = true;
        break;
      }
      if (!hasProcessing && (item.parse_status === 'PROCESSING' || item.parse_status === 'PENDING' ||
          this.isResolutionParseProcessing(item.resolution_parse_status) ||
          this.isResolutionConvertProcessing(item) ||
          item.convert_status === 'PROCESSING' || item.convert_status === 'PENDING')) {
        hasProcessing = true;
      }
    }

    if (hasFailed) {
      return [...templateChipList, ...failChipList];
    }
    if (hasProcessing) {
      return [...templateChipList, ...processingChipList];
    }
    return templateChipList;
  },
  isTemplateSectionValid(tableOfContentTemplate, meetingTemplate, contents_template_variable, meeting_template_variable) {
    const meetingContentTemplateItems = tableOfContentTemplate?.variables ?? [];
    const meetingMeetingTemplateItems = meetingTemplate?.variables ?? [];
    const groupContentTemplateSectionId = contents_template_variable?.id ?? '';
    const groupMeetingTemplateSectionId = meeting_template_variable?.id ?? '';

    // 檢查目錄範本區塊
    const hasTableOfContentTemplate = tableOfContentTemplate && tableOfContentTemplate.id !== '' && meetingContentTemplateItems.length > 0;
    // 如果 template 存在且有 variables，但 section 沒選或選的 id 不在 variables 中，則無效
    const isTableOfContentSectionInvalid = hasTableOfContentTemplate &&
      !meetingContentTemplateItems.some(item => item.id === groupContentTemplateSectionId);

    // 檢查議程範本區塊
    const hasMeetingTemplate = meetingTemplate && meetingTemplate.id !== '' && meetingMeetingTemplateItems.length > 0;
    // 如果 template 存在且有 variables，但 section 沒選或選的 id 不在 variables 中，則無效
    const isMeetingSectionInvalid = hasMeetingTemplate &&
      !meetingMeetingTemplateItems.some(item => item.id === groupMeetingTemplateSectionId);

    return {
      isTableOfContentSectionInvalid,
      isMeetingSectionInvalid,
    };
  },
  // 決議解析 PENDING 代表沒輸入決議或沒選決議範本，不算失敗也不算處理中
  isResolutionParseFailed(status) {
    return status === 'FAILED';
  },
  isResolutionParseProcessing(status) {
    return status === 'PROCESSING';
  },
  // 決議轉檔在解析 COMPLETE 後才開始，PENDING 只在解析完成時算轉檔中
  isResolutionConvertFailed({ resolution_convert_status }) {
    return resolution_convert_status === 'FAILED';
  },
  isResolutionConvertProcessing({ resolution_parse_status, resolution_convert_status }) {
    return (
      resolution_convert_status === 'PROCESSING' ||
      (resolution_convert_status === 'PENDING' && resolution_parse_status === 'COMPLETE')
    );
  },
  setItemChipList(item) {
    if (item.parse_status === 'FAILED') {
      return [{
        text: '解析失敗',
        color: 'red',
        value: 'failed',
      }];
    }
    if (item.parse_status === 'PROCESSING' || item.parse_status === 'PENDING') {
      return [{
        text: '解析中',
        color: 'red',
        value: 'processing',
      }];
    }
    // 決議解析狀態與提案單解析同樣處理，優先於轉檔狀態
    if (this.isResolutionParseFailed(item.resolution_parse_status)) {
      return [{
        text: '解析失敗',
        color: 'red',
        value: 'failed',
      }];
    }
    if (this.isResolutionParseProcessing(item.resolution_parse_status)) {
      return [{
        text: '解析中',
        color: 'red',
        value: 'processing',
      }];
    }
    if (this.isResolutionConvertFailed(item)) {
      return [{
        text: '轉檔失敗',
        color: 'red',
        value: 'failed',
      }];
    }
    if (this.isResolutionConvertProcessing(item)) {
      return [{
        text: '轉檔中',
        color: 'yellow',
        value: 'processing',
      }];
    }
    if (item.convert_status === 'FAILED') {
      return [{
        text: '轉檔失敗',
        color: 'red',
        value: 'failed',
      }];
    }
    if (item.convert_status === 'PROCESSING' || item.convert_status === 'PENDING') {
      return [{
        text: '轉檔中',
        color: 'yellow',
        value: 'processing',
      }];
    }
    return [];
  },
  setIndexMeetingItem(rawDataList) {
    return rawDataList.map(({
      id,
      name,
      item_label,
      sequence,
      convert_status,
      parse_status,
      resolution_parse_status,
      resolution_convert_status,
      duration,
      start_time,
      end_time,
    }) => {
      const baseData = {
        id,
        name: this.decodeName(name),
        sequence,
        caseNumber: item_label,
        isCollapsable: true,
        isLoading: false,
        duration: duration ?? null,
        startAt: H_time.getDateDashFormat(start_time, 'YYYY-MM-DD HH:mm:ss'),
        endAt: H_time.getDateDashFormat(end_time, 'YYYY-MM-DD HH:mm:ss'),
        meetingItemUnit: '',
        presenter: '',
        attendeesPresent: '',
        attendeesInvited: '',
        attaches: [],
        meetingItemFile: [],
        resolutionSummary: '',
        resolutionDetails: '',
        resolutionAttaches: [],
        convertResolutionAttach: null,
        resolutionParseStatus: resolution_parse_status,
        resolutionConvertStatus: resolution_convert_status,
        convertPdf: [],
        convertAttach: [],
        chipList: this.setItemChipList({
          convert_status,
          parse_status,
          resolution_parse_status,
          resolution_convert_status,
        }),
        disable: this.setItemDisable({ parse_status, convert_status }),
        convertStatus: convert_status,
        parseStatus: parse_status,
      };
      return baseData;
    })
  },
  setShowMeetingItem(rawData) {
    const {
      id,
      name,
      item_label,
      attendees_invited,
      attendees_present,
      convert_status,
      meeting_item_attaches,
      meeting_item_unit,
      original_attach,
      parse_status,
      presenter,
      resolution_details,
      resolution_summary,
      resolution_attaches,
      convert_resolution_attach,
      resolution_parse_status,
      resolution_convert_status,
      sequence,
      convert_pdf_attach,
      convert_attach,
      updated_at,
    } = rawData;
    return {
      id,
      name: this.decodeName(name),
      sequence,
      caseNumber: item_label,
      isCollapsable: true,
      meetingItemUnit: meeting_item_unit,
      presenter,
      attendeesPresent: attendees_present,
      attendeesInvited: attendees_invited,
      attaches: this.setShowMeetingItemAttaches(meeting_item_attaches),
      meetingItemFile: original_attach ? H_file.setShowInModule([original_attach])[0] : null,
      resolutionSummary: resolution_summary,
      resolutionDetails: resolution_details,
      resolutionAttaches: this.setShowMeetingItemAttaches(resolution_attaches ?? []),
      convertResolutionAttach: convert_resolution_attach
        ? H_file.setShowInModule([convert_resolution_attach])[0]
        : null,
      resolutionParseStatus: resolution_parse_status,
      resolutionConvertStatus: resolution_convert_status,
      convertPdf: convert_pdf_attach ? H_file.setShowInModule([convert_pdf_attach])[0] : null,
      convertAttach: convert_attach ? H_file.setShowInModule([convert_attach])[0] : null,
      chipList: this.setItemChipList({
        parse_status,
        convert_status,
        resolution_parse_status,
        resolution_convert_status,
      }),
      disable: this.setItemDisable({ parse_status, convert_status }),
      convertStatus: convert_status,
      parseStatus: parse_status,
      updatedAt: H_time.getDateDashFormat(updated_at, 'YYYY-MM-DD HH:mm:ss'),
    };
  },
  setSubmitMeetingItemOriginalAttach(rawData) {
    const { caseNumber, file } = rawData;
    const formData = new FormData();
    formData.append('file', file[0].file);
    formData.append('factory', store.state.app.factory.id);
    formData.append('item_label', caseNumber);
    return formData;
  },
  setSubmitMeetingItemUpdateData(rawData) {
    const {
      name,
      caseNumber,
      meetingItemUnit,
      presenter,
      attendeesInvited,
      attendeesPresent,
      attaches,
      resolutionSummary,
      resolutionDetails,
    } = rawData;
    return {
      factory: store.state.app.factory.id,
      name,
      item_label: caseNumber,
      meeting_item_unit: meetingItemUnit,
      presenter,
      attendees_invited: attendeesInvited,
      attendees_present: attendeesPresent,
      resolution_summary: resolutionSummary,
      resolution_details: resolutionDetails,
      meeting_item_attaches: this.setSubmitMeetingItemAttaches(attaches),
    };
  },
  setSubmitMeetingItemResolutionData({ resolution, resolutionAttaches }) {
    return {
      factory: store.state.app.factory.id,
      resolution_details: resolution,
      resolution_attaches: this.setSubmitMeetingItemAttaches(resolutionAttaches, 'resolution'),
    };
  },
  setSubmitMeetingItemAttaches(attaches, type = 'item') {
    const factory = store.state.app.factory.id;
    return attaches.map(({
      meetingItemAttachId,
      isExportName,
      exportName,
      exportNameLocation,
      ...attach
    }, index) => ({
      id: meetingItemAttachId,
      factory,
      sequence: String(index + 1).padStart(4, '0'),
      type,
      original_attach: H_file.setSubmitInModule([attach])[0],
      name: exportName,
      has_frame: isExportName ? true : false,
      frame_position: exportNameLocation,
    }));
  },
  setShowMeetingItemAttaches(attaches) {
    return attaches.map(({
      id,
      name,
      has_frame,
      frame_position,
      original_attach,
    }) => {
      const fileInfo = H_file.setShowInModule([original_attach])[0];
      const isCompressed = fileInfo?.isCompressed;
      return {
        ...fileInfo,
        exportName: name,
        isExportName: has_frame === 1,
        meetingItemAttachId: id,
        compressStatus: isCompressed
          ? [{
            name: '轉檔完成',
            color: 'medium-blue',
          }] : [{
            name: '轉檔中',
            color: 'yellow',
          }],
        exportNameLocation: frame_position,
      }
    });
  },
  setExportNameLocationText(exportNameLocation) {
    const tarItem = Spare_meeting.exportNameLocationRadioItems.buttonItems.find(
      ({ id }) => id === exportNameLocation,
    );
    return tarItem?.textLabel ?? '';
  },
  // 後端沒有「自訂開始時間」旗標：開始時間不等於前一筆結束時間（第一筆為議事開始時間）就視為自訂
  setSortList(rawDataList, startAt) {
    let cursor = startAt || '';
    return rawDataList
      .map(({ id, name, children = [] }) => ({
        id,
        title: name,
        list: children.map(
          ({ id: itemId, name, caseNumber, duration, startAt: itemStartAt, endAt }) => {
            const customStartTime =
              itemStartAt && itemStartAt !== cursor ? dayjs(itemStartAt).format('HH:mm') : null;
            cursor = endAt || cursor;
            return {
              id: itemId,
              name,
              caseNumber,
              duration,
              customStartTime,
            };
          },
        ),
      }));
  },
  // 依序計算每個議案的時間區間：有自訂開始時間就用它，否則接在前一個議案結束之後
  $_calcItemSchedule(items, startAt) {
    const format = 'YYYY-MM-DD HH:mm:ss';
    let cursor = startAt ? dayjs(startAt) : null;
    return items.map(({ duration, customStartTime }) => {
      const start = customStartTime
        ? dayjs(`${(cursor || dayjs()).format('YYYY-MM-DD')} ${customStartTime}:00`)
        : cursor;
      const end = start ? start.add(Number(duration) || 0, 'minute') : null;
      cursor = end || cursor;
      return {
        startAt: start ? start.format(format) : '',
        endAt: end ? end.format(format) : '',
      };
    });
  },
  setScheduledSortList(sortList, startAt) {
    const flatItems = [].concat(...sortList.map(group => group.list));
    const times = this.$_calcItemSchedule(flatItems, startAt);
    let index = 0;
    return sortList.map(group => ({
      ...group,
      list: group.list.map(item => ({ ...item, ...times[index++] })),
    }));
  },
  setSortItemCustomStartTime(sortList, startAt, itemId, time) {
    const setCustomStartTime = customStartTime => sortList.map(group => ({
      ...group,
      list: group.list.map(item => (item.id === itemId ? { ...item, customStartTime } : item)),
    }));
    const autoSortList = this.setScheduledSortList(setCustomStartTime(null), startAt);
    const autoItem = [].concat(...autoSortList.map(group => group.list)).find(item => item.id === itemId);
    // 新時間跟自動計算的一樣，就不算手動調整
    if (autoItem && autoItem.startAt && dayjs(autoItem.startAt).format('HH:mm') === time) {
      return autoSortList;
    }
    return this.setScheduledSortList(setCustomStartTime(time), startAt);
  },
  setMeetingItemTimeMap(meetingItemGroup) {
    const items = {};
    meetingItemGroup.forEach(({ children = [] }) => {
      children.forEach(({ id, duration, startAt, endAt }) => {
        items[id] = { duration, startAt, endAt };
      });
    });
    const groups = {};
    meetingItemGroup.forEach(({ id, children = [] }) => {
      const timedItems = children
        .map(child => items[child.id])
        .filter(item => item.duration !== null && item.startAt);
      groups[id] = timedItems.length > 0
        ? {
          duration: timedItems.reduce((sum, item) => sum + item.duration, 0),
          startAt: timedItems[0].startAt,
          endAt: timedItems[timedItems.length - 1].endAt,
        }
        : { duration: null, startAt: '', endAt: '' };
    });
    return { items, groups };
  },
  // 所有議案時長加總（分鐘），沒有任何議案設定時長時回 null
  getTotalDuration(meetingItemGroup) {
    const durations = meetingItemGroup
      .flatMap(({ children = [] }) => children)
      .map(({ duration }) => duration)
      .filter(duration => duration !== null);
    if (durations.length === 0) return null;
    return durations.reduce((sum, duration) => sum + duration, 0);
  },
  // 分鐘轉成「x時x分」文字
  setHourMinuteText(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}${i18n.t('時')}${mins}${i18n.t('分')}`;
  },
  // 議事時間送出用 ISO 8601 帶時區（例：2026-10-10T10:10:00+08:00），由後端轉 UTC 儲存
  $_toIsoWithOffset(time) {
    return time ? dayjs(time).format('YYYY-MM-DDTHH:mm:ssZ') : null;
  },
  setSubmitSortList(rawDataList, itemLabelRegex, meetingStartTime) {
    return {
      factory: store.state.app.factory.id,
      meeting_start_time: this.$_toIsoWithOffset(meetingStartTime),
      meeting_item_groups: rawDataList.map(({id: itemGroupId, list}, itemGroupIndex) => ({
        id: itemGroupId,
        sequence: String(itemGroupIndex + 1).padStart(4, '0'),
        meeting_items: list.map((item, itemIndex) => {
          const sequenceNum = itemIndex + 1;
          return {
            id: item.id,
            sequence: String(sequenceNum).padStart(4, '0'),
            item_label: this.generateItemLabel(itemLabelRegex, sequenceNum),
            meeting_item_group: itemGroupId,
            duration: item.duration ?? null,
            start_time: this.$_toIsoWithOffset(item.startAt),
            end_time: this.$_toIsoWithOffset(item.endAt),
          };
        })
      }))
    };
  },
  setSubmitUpdateGroupList(rawDataList) {
    return rawDataList.map(({
      id,
      name,
      newScheduleTemplate,
      newContentsTemplateSection,
      newMeetingTemplateSection,
    }) => ({
      id,
      name,
      factory: store.state.app.factory.id,
      schedule_template: newScheduleTemplate,
      contents_template_variable: newContentsTemplateSection,
      meeting_template_variable: newMeetingTemplateSection,
    }));
  },
  setShowExportRecord(rawData) {
    const {
      name,
      created_at,
      created_user,
      convert_status,
      remark,
      meeting_export_items,
      type,
      attach_location_type,
      convert_pdf_attach,
      convert_word_attach,
      convert_attach,
      has_page,
      page_position,
    } = rawData;
    return {
      name,
      createdAt: H_time.getDateDashFormat(created_at, 'YYYY-MM-DD HH:mm:ss'),
      createdUser: created_user,
      convertStatus: this.$_getConvertStatusName(convert_status),
      remark,
      meetingExportItems: meeting_export_items.map(({ id, module, meeting_item }) => ({ id, module, meetingItem:meeting_item })),
      type,
      attachLocationType: this.$_getAttachLocationTypeText(attach_location_type),
      attachLocationTypeValue: attach_location_type,
      convertPdfAttach: convert_pdf_attach ? H_file.setShowInModule([convert_pdf_attach]) : [],
      convertWordAttach: convert_word_attach
        ? H_file.setShowInModule([convert_word_attach])[0]
        : null,
      convertAttach: convert_attach
        ? H_file.setShowInModule([convert_attach])[0]
        : null,
      isDisplayPageNumber: has_page,
      pageNumberLocation: this.getPageNumberLocationText(page_position),
    };
  },
  getPageNumberLocationText(pageNumberLocation) {
    const items = Spare_meeting.pageNumberLocationRadioItems.buttonItems;
    return items.find(item => item.id === pageNumberLocation)?.textLabel;
  },
  $_getAttachLocationTypeText(attachLocationType) {
    const items = Spare_meeting.attachLocationTypeRadioItems.buttonItems;
    return items.find(item => item.value === attachLocationType)?.textLabel;
  },
  setSubmitMeetingExportRecord(rawData) {
    const {
      name,
      remark,
      fields,
      attachLocationType,
      isDisplayPageNumber = 'show',
      pageNumberLocation = 'bottom-center',
      isExportResolution = 'yes',
    } = rawData;
    const factory = store.state.app.factory.id;
    return {
      factory,
      type: 'meeting',
      name,
      remark,
      export_type: isExportResolution === 'yes' ? 'resolution' : 'item',
      meeting_export_items: this.setSubmitExportRecordItems(
        fields,
        factory,
        attachLocationType,
        isExportResolution,
      ),
      attach_location_type: this.$_getAttachLocationTypeValue(attachLocationType),
      has_page: isDisplayPageNumber === 'show',
      page_position: pageNumberLocation,
    };
  },
  $_getAttachLocationTypeValue(attachLocationType) {
    const items = Spare_meeting.attachLocationTypeRadioItems.buttonItems;
    return items.find(item => item.id === attachLocationType)?.value;
  },
  setSubmitExportRecordItems(fields, factory, attachLocationType, isExportResolution = 'yes') {
    const moduleIds = ['cover', 'contents', 'attach_cover'];
    return fields
      .filter(item =>
        item.isChecked &&
        (moduleIds.includes(item.value) || item.parentId !== undefined) &&
        (item.value !== 'attach_cover' || (item.value === 'attach_cover' && attachLocationType === 1))
      )
      .map(({ id, value, isExportAttach, isExportResolutionAttach }) => {
        const isMeetingItem = !moduleIds.includes(value);
        return {
          factory,
          ...(isMeetingItem ? { meeting_item: id } : { module: value }),
          has_item_attaches: isMeetingItem && !!isExportAttach,
          // 不匯出決議時 toggle 已隱藏，一律送 false
          has_resolution_attaches:
            isMeetingItem && isExportResolution === 'yes' && !!isExportResolutionAttach,
        };
      });
  },
  setChangedSortList(sortList, itemLabelRegex) {
    return sortList.map(group => ({
      ...group,
      list: group.list.map((item, itemIndex) => ({
        ...item,
        caseNumber: this.generateItemLabel(itemLabelRegex, itemIndex + 1),
      })),
    }));
  },
  setSubmitScheduleExportRecord(rawData) {
    const { name, remark } = rawData;
    const factory = store.state.app.factory.id;
    return {
      factory,
      name,
      remark,
    };
  },
  setItemDisable({ parse_status = '', convert_status = '' }) {
    return parse_status === 'PROCESSING' || parse_status === 'PENDING' ||
      (convert_status === 'PENDING' && parse_status === 'COMPLETE') ||
      (convert_status === 'PROCESSING');
  },
  setCompressingAttachList(rawDataList) {
    if (!rawDataList || rawDataList.length === 0) return [];
    const groupMap = new Map();
    for (const { name, item_label, meeting_item_group } of rawDataList) {
      const itemName = `${item_label} ${name}`;
      const { id: groupId, name: groupName } = meeting_item_group;
      if (!groupMap.has(groupId)) {
        groupMap.set(groupId, { groupId, groupName, itemList: [] });
      }
      groupMap.get(groupId).itemList.push(itemName);
    }
    return Array.from(groupMap.values());
  },
};
